import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Spin,
  message,
  Row,
  Col,
  Select,
  Input,
  Button,
  Space,
  Modal,
  Typography,
  Form,
  DatePicker,
  Tooltip,
  Statistic,
  Divider,
  Badge,
  Switch
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
  ShoppingOutlined,
  BulbOutlined,
  BulbFilled
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getRefundRequests, approveRefund, rejectRefund } from "../../../services/admin/refundService";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Demandes = () => {
  const { t } = useTranslation();
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    statut: 'all',
    search: '',
    dateRange: []
  });
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectForm] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);


 

  useEffect(() => {
    fetchDemandes();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchDemandes = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        statut: filters.statut !== 'all' ? filters.statut : undefined,
        search: filters.search || undefined,
        start_date: filters.dateRange && filters.dateRange[0] ? filters.dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: filters.dateRange && filters.dateRange[1] ? filters.dateRange[1].format('YYYY-MM-DD') : undefined
      };

      const response = await getRefundRequests(params);

      if (response.success) {
        setDemandes(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.total || response.data.length
        }));
      } else {
        message.error(response.message || t('ErrorLoadingData'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error(t('ServerError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleFilter = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchDemandes();
  };

  const handleReset = () => {
    setFilters({
      statut: 'all',
      search: '',
      dateRange: []
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showDetails = (demande) => {
    setSelectedDemande(demande);
    setDetailModalVisible(true);
  };

  const handleApprove = async (demandeId) => {
    try {
      setActionLoading(true);
      const response = await approveRefund(demandeId);

      if (response.success) {
        message.success(t('RefundApprovedSuccess'));
        fetchDemandes();
      } else {
        message.error(response.message || t('ActionFailed'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error(t('ServerError'));
    } finally {
      setActionLoading(false);
    }
  };

  const showRejectModal = (demande) => {
    setSelectedDemande(demande);
    setRejectModalVisible(true);
  };

  const handleReject = async (values) => {
    try {
      setActionLoading(true);
      const response = await rejectRefund(selectedDemande.id, values.reponse_admin);

      if (response.success) {
        message.success(t('RefundRejectedSuccess'));
        setRejectModalVisible(false);
        rejectForm.resetFields();
        fetchDemandes();
      } else {
        message.error(response.message || t('ActionFailed'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error(t('ServerError'));
    } finally {
      setActionLoading(false);
    }
  };

  // Format de date sans dayjs
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusTag = (statut) => {
    const config = {
      'en_attente': { color: 'orange', icon: <ClockCircleOutlined />, text: t('Pending') },
      'approuve': { color: 'green', icon: <CheckCircleOutlined />, text: t('Approved') },
      'refuse': { color: 'red', icon: <CloseCircleOutlined />, text: t('Rejected') }
    };

    const statusConfig = config[statut] || config['en_attente'];

    return (
      <Tag color={statusConfig.color} icon={statusConfig.icon}>
        {statusConfig.text}
      </Tag>
    );
  };

  const getStatusBadge = (statut) => {
    const config = {
      'en_attente': 'processing',
      'approuve': 'success',
      'refuse': 'error'
    };
    return config[statut] || 'default';
  };

  const columns = [
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('RefundID')}</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => <Text strong style={{ color: "var(--primary-color)" }}>#{id}</Text>
    },
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('User')}</span>,
      dataIndex: ['user', 'name'],
      key: 'user',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: "var(--primary-color)" }}>{record.user?.username}</Text>
          <Text style={{ fontSize: '12px', color: "var(--primary-color)" }}>
            {record.user?.email}
          </Text>
        </Space>
      )
    },
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('Order')}</span>,
      dataIndex: ['achat', 'offre', 'titre'],
      key: 'order',
      render: (titre, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px', color: "var(--primary-color)" }}>
            {t('Amount')}: {record.achat?.montant} $
          </Text>
          <Text style={{ fontSize: '12px', color: "var(--primary-color)" }}>
            {t('OrderID')}: #{record.achat_offre_id}
          </Text>
        </Space>
      )
    },
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('RefundReason')}</span>,
      dataIndex: 'raison',
      key: 'raison',
      ellipsis: true,
      render: (raison) => (
        <Tooltip title={raison}>
          <Text style={{ color: "var(--primary-color)" }}>
            {raison?.length > 50 ? `${raison.substring(0, 50)}...` : raison}
          </Text>
        </Tooltip>
      )
    },
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('RequestDate')}</span>,
      dataIndex: 'date_demande',
      key: 'date_demande',
      render: (date) => <span style={{ color: "var(--primary-color)" }}>{formatDate(date)}</span>,
      sorter: (a, b) => new Date(a.date_demande) - new Date(b.date_demande)
    },
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('Status')}</span>,
      dataIndex: 'statut',
      key: 'statut',
      render: (statut) => getStatusTag(statut),
      filters: [
        { text: t('Pending'), value: 'en_attente' },
        { text: t('Approved'), value: 'approuve' },
        { text: t('Rejected'), value: 'refuse' }
      ],
      onFilter: (value, record) => record.statut === value
    },
    {
      title: <span style={{ color: "var(--primary-color)" }}>{t('Actions')}</span>,
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => showDetails(record)}
            size="small"
            style={{ 
              backgroundColor: "var(--primary-color3)",
              borderColor: "var(--primary-color3)"
            }}
          >
            {t('View')}
          </Button>
          {record.statut === 'en_attente' && (
            <>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprove(record.id)}
                size="small"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                loading={actionLoading}
              >
                {t('Approve')}
              </Button>
              <Button
                type="primary"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => showRejectModal(record)}
                size="small"
                loading={actionLoading}
              >
                {t('Reject')}
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  const stats = {
    total: demandes.length,
    pending: demandes.filter(d => d.statut === 'en_attente').length,
    approved: demandes.filter(d => d.statut === 'approuve').length,
    rejected: demandes.filter(d => d.statut === 'refuse').length
  };

  return (
    <div style={{ 
      padding: '24px',
      backgroundColor: "var(--background-body)",
      minHeight: '100vh',
      color: "var(--primary-color)"
    }}>
      {/* En-tête avec titre et switch mode sombre/clair */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <Title level={2} style={{ color: "var(--primary-color)", margin: 0 }}>
            {t('RefundRequests')}
          </Title>
          <Text style={{ color: "var(--primary-color)", opacity: 0.8 }}>
            {t('ManageRefundRequests')}
          </Text>
        </div>
        
      </div>

      <Card
        style={{ 
          backgroundColor: "var(--background-body)",
          border: "1px solid var(--primary-color6)"
        }}
        bodyStyle={{ padding: '16px' }}
      >
        {/* Statistiques */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card 
              size="small"
              style={{ 
                backgroundColor: "var(--background-body)",
                border: "1px solid var(--primary-color6)"
              }}
            >
              <Statistic
                title={<span style={{ color: "var(--primary-color)" }}>{t('TotalRequests')}</span>}
                value={stats.total}
                prefix={<ShoppingOutlined style={{ color: "var(--primary-color3)" }} />}
                valueStyle={{ color: "var(--primary-color)" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card 
              size="small"
              style={{ 
                backgroundColor: "var(--background-body)",
                border: "1px solid var(--primary-color6)"
              }}
            >
              <Statistic
                title={<span style={{ color: "var(--primary-color)" }}>{t('Pending')}</span>}
                value={stats.pending}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card 
              size="small"
              style={{ 
                backgroundColor: "var(--background-body)",
                border: "1px solid var(--primary-color6)"
              }}
            >
              <Statistic
                title={<span style={{ color: "var(--primary-color)" }}>{t('Approved')}</span>}
                value={stats.approved}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card 
              size="small"
              style={{ 
                backgroundColor: "var(--background-body)",
                border: "1px solid var(--primary-color6)"
              }}
            >
              <Statistic
                title={<span style={{ color: "var(--primary-color)" }}>{t('Rejected')}</span>}
                value={stats.rejected}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtres */}
        <Card 
          title={
            <Space>
              <FilterOutlined style={{ color: "var(--primary-color)" }} />
              <span style={{ color: "var(--primary-color)" }}>{t('Filters')}</span>
            </Space>
          }
          style={{ 
            marginBottom: 16,
            backgroundColor: "var(--background-body)",
            border: "1px solid var(--primary-color6)"
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8} md={6}>
              <Select
                value={filters.statut}
                onChange={(value) => setFilters(prev => ({ ...prev, statut: value }))}
                placeholder={t('Status')}
                style={{ width: '100%' }}
                dropdownStyle={{
                  backgroundColor: "var(--background-body)",
                  border: "1px solid var(--primary-color6)"
                }}
              >
                <Option value="all">{t('AllStatuses')}</Option>
                <Option value="en_attente">{t('Pending')}</Option>
                <Option value="approuve">{t('Approved')}</Option>
                <Option value="refuse">{t('Rejected')}</Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder={t('SearchByNameEmailOrReason')}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                suffix={<SearchOutlined style={{ color: "var(--primary-color)" }} />}
                style={{
                  backgroundColor: "var(--background-body)",
                  color: "var(--primary-color)",
                  border: "1px solid var(--primary-color6)"
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Space>
                <Button 
                  type="primary" 
                  icon={<FilterOutlined />} 
                  onClick={handleFilter}
                  style={{ 
                    backgroundColor: "var(--primary-color3)",
                    borderColor: "var(--primary-color3)"
                  }}
                >
                  {t('Filter')}
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={handleReset}
                  style={{ color: "var(--primary-color)" }}
                >
                  {t('Reset')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tableau */}
        <Table
          columns={columns}
          dataSource={demandes}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${t('Showing')} ${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          style={{ 
            backgroundColor: "var(--background-body)",
          }}
        />
      </Card>

      {/* Modal de détails */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "var(--primary-color3)" }} />
            <span style={{ color: "var(--primary-color)" }}>
              {t('RefundRequestDetails')} #{selectedDemande?.id}
            </span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button 
            key="close" 
            onClick={() => setDetailModalVisible(false)}
            style={{ 
              backgroundColor: "var(--primary-color3)",
              borderColor: "var(--primary-color3)",
              color: 'white'
            }}
          >
            {t('Close')}
          </Button>
        ]}
        width={700}
        styles={{
          body: {
            backgroundColor: "var(--background-body)",
            color: "var(--primary-color)"
          }
        }}
      >
        {selectedDemande && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card 
                  size="small" 
                  title={<span style={{ color: "var(--primary-color)" }}>{t('UserInformation')}</span>}
                  style={{ 
                    backgroundColor: "var(--background-body)",
                    border: "1px solid var(--primary-color6)"
                  }}
                >
                  <Space direction="vertical">
                    <Text strong style={{ color: "var(--primary-color)" }}>{selectedDemande.user?.name}</Text>
                    <Text style={{ color: "var(--primary-color)" }}>{selectedDemande.user?.email}</Text>
                    <Text style={{ color: "var(--primary-color)" }}>ID: {selectedDemande.user_id}</Text>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card 
                  size="small" 
                  title={<span style={{ color: "var(--primary-color)" }}>{t('RequestInformation')}</span>}
                  style={{ 
                    backgroundColor: "var(--background-body)",
                    border: "1px solid var(--primary-color6)"
                  }}
                >
                  <Space direction="vertical">
                    <Badge status={getStatusBadge(selectedDemande.statut)} text={getStatusTag(selectedDemande.statut)} />
                    <Text style={{ color: "var(--primary-color)" }}>
                      {t('RequestDate')}: {formatDate(selectedDemande.date_demande)}
                    </Text>
                    {selectedDemande.date_traitement && (
                      <Text style={{ color: "var(--primary-color)" }}>
                        {t('ProcessingDate')}: {formatDate(selectedDemande.date_traitement)}
                      </Text>
                    )}
                  </Space>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Card 
              size="small" 
              title={<span style={{ color: "var(--primary-color)" }}>{t('OrderDetails')}</span>} 
              style={{ 
                marginBottom: 16,
                backgroundColor: "var(--background-body)",
                border: "1px solid var(--primary-color6)"
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text strong style={{ color: "var(--primary-color)" }}>{t('Product')}: </Text>
                  <Text style={{ color: "var(--primary-color)" }}>{selectedDemande.achat?.offre?.titre}</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ color: "var(--primary-color)" }}>{t('Amount')}: </Text>
                  <Text style={{ color: '#52c41a' }}>{selectedDemande.achat?.montant} $</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ color: "var(--primary-color)" }}>{t('OrderDate')}: </Text>
                  <Text style={{ color: "var(--primary-color)" }}>
                    {formatDate(selectedDemande.achat?.date_achat)}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ color: "var(--primary-color)" }}>{t('OrderStatus')}: </Text>
                  <Tag color={
                    selectedDemande.achat?.etat === 'enattente' ? 'orange' :
                    selectedDemande.achat?.etat === 'confirme' ? 'green' : 'red'
                  }>
                    {selectedDemande.achat?.etat}
                  </Tag>
                </Col>
              </Row>
            </Card>

            <Card 
              size="small" 
              title={<span style={{ color: "var(--primary-color)" }}>{t('RefundReason')}</span>}
              style={{ 
                backgroundColor: "var(--background-body)",
                border: "1px solid var(--primary-color6)"
              }}
            >
              <Text style={{ color: "var(--primary-color)" }}>{selectedDemande.raison}</Text>
            </Card>

            {selectedDemande.reponse_admin && (
              <Card 
                size="small" 
                title={<span style={{ color: "var(--primary-color)" }}>{t('AdminResponse')}</span>} 
                style={{ 
                  marginTop: 16,
                  backgroundColor: "var(--background-body)",
                  border: "1px solid var(--primary-color6)"
                }}
              >
                <Text style={{ 
                  color: selectedDemande.statut === 'refuse' ? '#ff4d4f' : '#52c41a'
                }}>
                  {selectedDemande.reponse_admin}
                </Text>
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de rejet */}
      <Modal
        title={
          <Space>
            <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
            <span style={{ color: "var(--primary-color)" }}>
              {t('RejectRefundRequest')} #{selectedDemande?.id}
            </span>
          </Space>
        }
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          rejectForm.resetFields();
        }}
        footer={null}
        styles={{
          body: {
            backgroundColor: "var(--background-body)",
            color: "var(--primary-color)"
          }
        }}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          onFinish={handleReject}
        >
          <Form.Item
            name="reponse_admin"
            label={<span style={{ color: "var(--primary-color)" }}>{t('RejectionReason')}</span>}
            rules={[
              { required: true, message: t('PleaseProvideRejectionReason') },
              { min: 10, message: t('ReasonMustBeAtLeast10Characters') }
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t('ExplainWhyYouRejectThisRequest')}
              style={{
                backgroundColor: "var(--background-body)",
                color: "var(--primary-color)",
                border: "1px solid var(--primary-color6)"
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                danger 
                htmlType="submit" 
                loading={actionLoading}
              >
                {t('ConfirmRejection')}
              </Button>
              <Button 
                onClick={() => {
                  setRejectModalVisible(false);
                  rejectForm.resetFields();
                }}
                style={{ color: "var(--primary-color)" }}
              >
                {t('Cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Demandes;